/**
 * SharedPlan: public read-only view of a shared district plan (/p/:token).
 *
 * No login required to view or co-sign -- the share token is the capability.
 * Making a copy or submitting requires a free account (the copy lands in
 * the signer's drafts). Renders the plan on the map in district colors
 * (MapShell in view-only mode), a read-only statistics summary, and three
 * actions in friction order:
 *
 *   1. Co-sign ("I support this plan") -- one tap, public advocacy.
 *   2. Make a copy -- fork into your own editable draft, then adjust.
 *   3. Submit as-is -- forks, then drops you in the editor at the submit step.
 *
 * Mobile-first: most share links travel over text/WhatsApp.
 */
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../lib/api';
import { MapShell } from '../map/MapShell';
import { districtLegendItems, MapLegend, UNASSIGNED_LEGEND_ITEM } from '../components/map/MapLegend';
import { resolveDistrictColors, resolveDistrictLabels } from '../lib/districtColors';
import { mergeDistrictLabels } from '../lib/plans';
import { ExportMenu } from '../components/ExportMenu';
import { exportPlan } from '../lib/exports';
import { usePaintingStore } from '../stores/painting';
import { useRequireAuth } from '../hooks/useRequireAuth';
import { useTenantStore } from '../stores/tenant';
import {
  CosignDuplicateError,
  cosignPlan,
  forkPlan,
  getSharedPlan,
  type SharedPlan as SharedPlanData,
} from '../lib/sharing';
import type { TenantConfig } from '../lib/api';

function formatDeviation(pct: number): string {
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(2)}%`;
}

/** The author's district names over the jurisdiction's defaults. */
function planLabels(plan: SharedPlanData, tenant: TenantConfig): string[] {
  return mergeDistrictLabels(
    resolveDistrictLabels(plan.district_count, tenant.districts.labels),
    plan.district_labels,
  );
}

function SharedStats({ plan, tenant }: { plan: SharedPlanData; tenant: TenantConfig }) {
  const colors = resolveDistrictColors(plan.district_count, tenant.districts.colors);
  const labels = planLabels(plan, tenant);
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
      <h2 className="text-sm font-semibold text-ink">District statistics</h2>
      <p className="mt-1 text-xs text-stone-500">
        Ideal: {plan.ideal_population.toLocaleString('en-US')} people per district
      </p>
      <ul className="mt-3 space-y-2">
        {plan.districts.map((d) => {
          const over = Math.abs(d.deviation_pct) > 10;
          return (
            <li key={d.district} className="flex items-center gap-3 text-sm">
              <span
                aria-hidden="true"
                className="inline-block h-4 w-4 shrink-0 rounded-sm border border-black/10"
                style={{ backgroundColor: colors[d.district - 1] ?? '#94a3b8' }}
              />
              <span className="w-32 shrink-0 truncate font-medium text-stone-800" title={labels[d.district - 1]}>
                {labels[d.district - 1] ?? `District ${d.district}`}
              </span>
              <span className="flex-1 tabular-nums text-stone-600">
                {d.population.toLocaleString('en-US')}
              </span>
              <span
                className={`tabular-nums font-medium ${over ? 'text-red-600' : 'text-stone-700'}`}
              >
                {formatDeviation(d.deviation_pct)}
              </span>
            </li>
          );
        })}
      </ul>
      <p className="mt-3 text-xs text-stone-500">
        Largest deviation: {formatDeviation(plan.max_deviation_pct)} from ideal.
      </p>
    </div>
  );
}

function CosignModal({
  onClose,
  onSubmit,
  submitting,
  error,
}: {
  onClose: () => void;
  onSubmit: (displayName: string, contact: string) => void;
  submitting: boolean;
  error: string | null;
}) {
  const [displayName, setDisplayName] = useState('');
  const [contact, setContact] = useState('');
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cosign-title"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="cosign-title" className="text-base font-semibold text-ink">
          Co-sign this plan
        </h2>
        <p className="mt-1 text-sm text-stone-600">
          Your name is added to the plan&apos;s public supporter list.
        </p>
        <label className="mt-4 block text-sm text-stone-700">
          Your name
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g. Maria Santos"
            maxLength={120}
            className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm"
            autoFocus
          />
        </label>
        <label className="mt-3 block text-sm text-stone-700">
          Email <span className="text-stone-400">(optional — lets staff reach you)</span>
          <input
            type="email"
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            placeholder="you@example.org"
            maxLength={320}
            className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm"
          />
        </label>
        {error && (
          <p role="alert" className="mt-3 text-sm text-red-600">
            {error}
          </p>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg border border-stone-300 px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={submitting || displayName.trim().length === 0}
            onClick={() => onSubmit(displayName.trim(), contact.trim())}
            className="rounded-lg bg-tenant-primary px-4 py-2 text-sm font-medium text-white hover:bg-tenant-strong disabled:opacity-50"
          >
            {submitting ? 'Adding…' : 'Add my support'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function SharedPlan(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const tenant = useTenantStore((s) => s.tenant);
  document.title = 'Shared district plan';

  const [cosignOpen, setCosignOpen] = useState(false);
  const [cosigning, setCosigning] = useState(false);
  const [cosignError, setCosignError] = useState<string | null>(null);
  const [cosigned, setCosigned] = useState(false);
  const [forking, setForking] = useState(false);
  const [forkError, setForkError] = useState<string | null>(null);
  const { requireAuth, signInModal } = useRequireAuth();

  const planQuery = useQuery({
    queryKey: ['shared-plan', token],
    queryFn: () => getSharedPlan(token ?? ''),
    retry: false,
    enabled: !!token,
  });

  const plan = planQuery.data ?? null;
  const notFound =
    planQuery.error instanceof ApiError && planQuery.error.status === 404;

  async function handleCosign(displayName: string, contact: string): Promise<void> {
    if (!token) return;
    setCosigning(true);
    setCosignError(null);
    try {
      const count = await cosignPlan(token, displayName, contact);
      // Refresh the cached plan so the count updates everywhere.
      queryClient.setQueryData<SharedPlanData | undefined>(
        ['shared-plan', token],
        (old) => (old ? { ...old, cosign_count: count } : old),
      );
      setCosigned(true);
      setCosignOpen(false);
    } catch (error) {
      setCosignError(
        error instanceof CosignDuplicateError
          ? error.message
          : error instanceof Error
            ? error.message
            : 'Could not add your co-sign. Please try again.',
      );
    } finally {
      setCosigning(false);
    }
  }

  async function handleFork(submitAfter: boolean): Promise<void> {
    if (!token || !plan || !tenant || forking) return;
    setForking(true);
    setForkError(null);
    try {
      const detail = await forkPlan(token, plan.district_count);
      // Scope the painting store to this tenant FIRST: MapView's mount calls
      // setTenant, which resets plan state when the slug changes (including
      // from null). Loading the fork after scoping survives the navigation.
      const painting = usePaintingStore.getState();
      painting.setTenant(tenant.slug, tenant);
      painting.loadPlan(detail, tenant);
      navigate('/map', {
        state: {
          forkedFrom: plan.name,
          hint: submitAfter
            ? 'Copied — review it, then hit Submit plan when ready.'
            : 'Copied — adjust the boundaries, then validate and submit.',
        },
      });
    } catch (error) {
      setForkError(
        error instanceof Error ? error.message : 'Could not copy this plan. Please try again.',
      );
    } finally {
      setForking(false);
    }
  }

  if (!tenant) return <p className="p-6 text-sm text-stone-500">Loading…</p>;

  if (planQuery.isLoading) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 text-center">
        <p className="text-sm text-stone-500">Loading the shared plan…</p>
      </div>
    );
  }

  if (notFound || planQuery.isError || !plan) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 text-center">
        <h1 className="text-xl font-semibold text-ink">This link doesn&apos;t work</h1>
        <p className="mt-2 text-sm text-stone-600">
          The share link is invalid or was revoked by the plan&apos;s author.
        </p>
        <Link
          to="/"
          className="mt-6 inline-block rounded-lg bg-tenant-primary px-4 py-2 text-sm font-medium text-white hover:bg-tenant-strong"
        >
          Go to {tenant.name}&apos;s homepage
        </Link>
      </div>
    );
  }

  // The shared plan may draw a different district count than the tenant
  // default; the map colors follow the plan, not the default.
  const mapTenant: TenantConfig = {
    ...tenant,
    districts: { ...tenant.districts, count: plan.district_count, labels: planLabels(plan, tenant) },
  };

  return (
    <div className="mx-auto max-w-5xl space-y-4 px-4 py-6">
      {signInModal}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">
          Shared district plan · {tenant.name}
        </p>
        <h1 className="mt-1 text-2xl font-bold text-ink">{plan.name}</h1>
        <p className="mt-1 text-sm text-stone-600">
          Shared by a community member · {plan.district_count} districts ·{' '}
          <span className="font-medium text-stone-800">
            {plan.cosign_count} co-sign{plan.cosign_count === 1 ? '' : 's'}
          </span>{' '}
          · {plan.fork_count} {plan.fork_count === 1 ? 'copy' : 'copies'} made
        </p>
        <ExportMenu
          className="mt-3 inline-block"
          variant="secondary"
          label="Download"
          onExport={(format) =>
            exportPlan(format, {
              name: plan.name,
              districtCount: plan.district_count,
              assignments: plan.assignments,
              labels: planLabels(plan, tenant),
              colors: resolveDistrictColors(plan.district_count, tenant.districts.colors),
              comments: plan.comments,
            })
          }
        />
        {plan.comments && (
          <figure className="mt-4 rounded-xl border-l-4 border-tenant-primary bg-white px-4 py-3 shadow-card">
            <figcaption className="panel-title">From the author</figcaption>
            <blockquote className="mt-1.5 whitespace-pre-wrap text-sm leading-relaxed text-stone-700">
              {plan.comments}
            </blockquote>
          </figure>
        )}
      </div>

      <div className="relative overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm">
        <MapShell
          tenant={mapTenant}
          editable={false}
          initialAssignments={plan.assignments}
          className="h-[55vh] min-h-[320px] w-full"
        />
        <MapLegend
          items={[
            ...districtLegendItems(mapTenant.districts.count, mapTenant.districts.colors, mapTenant.districts.labels),
            UNASSIGNED_LEGEND_ITEM,
          ]}
          storageKey="map-legend:shared-plan"
          className="absolute bottom-3 left-3 z-20"
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <SharedStats plan={plan} tenant={tenant} />
        <div className="space-y-4">
          <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
            <h2 className="text-sm font-semibold text-ink">Support this plan</h2>
            {cosigned ? (
              <p role="status" className="mt-2 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                ✓ Thanks — your co-sign was added.
              </p>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setCosignError(null);
                  setCosignOpen(true);
                }}
                className="mt-3 w-full rounded-lg bg-tenant-primary px-4 py-2 text-sm font-medium text-white hover:bg-tenant-strong"
              >
                I support this plan
              </button>
            )}
            <button
              type="button"
              onClick={() =>
                requireAuth(
                  () => void handleFork(false),
                  'Sign in to copy this plan into your drafts and adjust its boundaries.',
                )
              }
              disabled={forking}
              className="mt-2 w-full rounded-lg border border-stone-300 bg-white px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-50"
            >
              {forking ? 'Copying…' : 'Make a copy to edit'}
            </button>
            <button
              type="button"
              onClick={() =>
                requireAuth(
                  () => void handleFork(true),
                  'Sign in to copy this plan and submit it as your own.',
                )
              }
              disabled={forking}
              className="mt-2 w-full rounded-lg px-4 py-2 text-sm font-medium text-tenant-primary underline-offset-2 hover:underline disabled:opacity-50"
            >
              Submit this plan as-is
            </button>
            {forkError && (
              <p role="alert" className="mt-2 text-sm text-red-600">
                {forkError}
              </p>
            )}
            <p className="mt-3 text-xs leading-relaxed text-stone-500">
              Co-signs are public shows of support — they are <em>not</em> official
              submissions to {tenant.name}. Making a copy lets you adjust the
              boundaries and submit the result yourself.
            </p>
          </div>
        </div>
      </div>

      {cosignOpen && (
        <CosignModal
          onClose={() => setCosignOpen(false)}
          onSubmit={(name, contact) => void handleCosign(name, contact)}
          submitting={cosigning}
          error={cosignError}
        />
      )}
    </div>
  );
}
